import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { bootstrapQueryOptions } from "~/app/api/pageData";
import { setRuntimeConfig } from "~/common/config/runtimeConfig";

/**
 * Triggers the Bootstrap Data fetch (GET /page-data/bootstrap) and mirrors formatting/currency/site values into the
 * runtime-config registry so `src/common` utils / `src/domains` services observe them (ADR 0003
 * interim layer-threading).
 *
 * Non-blocking boot gate (Slice 3): in non-blocking mode (legacy window.SCHA absent/empty — the
 * headless target), children are withheld (per-island skeleton = `null`) until the fetch resolves,
 * so they never render with default/empty config. During the Slice 1 transition the legacy
 * window.SCHA is still populated in production, so children render immediately (no added blank
 * flash) and the registry is re-seeded when the fetch lands. On fetch error, children render
 * anyway as a safe degradation.
 *
 * Per-island Suspense design (ADR 0003): each React island is wrapped in its own provider tree.
 * The first island to mount triggers the fetch; TanStack Query dedupes to a single network request.
 */
export function AppBootstrapProvider({ children }: { children: ReactNode }) {
  const { data, isError } = useQuery(bootstrapQueryOptions());

  if (data) {
    const { configuration, formatting } = data;
    const siteCode = (configuration.siteUid ?? "").slice(-2).toLowerCase();
    setRuntimeConfig({
      currencyIso: formatting.currencyIso ?? "",
      currencyDecimalDigits: formatting.currencyDecimalDigits ?? 2,
      decimalSeparator: formatting.decimalSeparator ?? "",
      thousandSeparator: formatting.thousandSeparator ?? "",
      dateFormat: formatting.dateFormat ?? "",
      datetimeFormat: formatting.datetimeFormat ?? "",
      timeFormat: formatting.timeFormat ?? "",
      timezoneOffset: formatting.timezoneOffset ?? 0,
      formattingLocale: formatting.userFormattingLocale ?? "en",
      cartPnaThreshold: Number(configuration.maxCartSizeWithPnaCall) || 0,
      siteUid: configuration.siteUid ?? "",
      isUsSite: siteCode === "us",
      removeSalesPartnerFinder: siteCode === "cn",
      marketingConsentEnabled: Boolean(configuration.marketingConsentEnabled),
      salesPartnerStockIntegrationEnabled: Boolean(configuration.salesPartnerStockIntegrationEnabled),
      batching: {
        interval: configuration.batching?.interval ?? 0,
        sizeResetTime: configuration.batching?.sizeResetTime ?? 0,
        pna: {
          priceEnabled: Boolean(configuration.batching?.pna?.priceEnabled),
          availabilityEnabled: Boolean(configuration.batching?.pna?.availabilityEnabled),
          batches: configuration.batching?.pna?.batches ?? [1],
        },
        lineItemUpdate: {
          enabled: Boolean(configuration.batching?.lineItemUpdate?.enabled),
          batches: configuration.batching?.lineItemUpdate?.batches ?? [1],
        },
      },
      rddMaxYearsAhead: configuration.rddMaxYearsAhead ?? 2,
      houseNumberFirstInAddress: Boolean(configuration.houseNumberFirstInAddress),
    });
    return <>{children}</>;
  }

  // Legacy transition: render immediately. Non-blocking mode + pending: skeleton (null).
  // Non-blocking mode + error: safe-degradation render.
  const legacyMode = Boolean(window.SCHA?.config && Object.keys(window.SCHA.config).length);
  return legacyMode || isError ? <>{children}</> : null;
}
